import type { ProjectDefinition } from "../../../types";
import { type FuncionProjectSpec, conditions, seededRandom } from "../spec";

const SHEET = "LG Reporte";
const TABLE = "Sucursales";
const GERENTES_TABLE = "Gerentes";

const GERENTES: [string, string][] = [
  ["G-01", "Laura Méndez"],
  ["G-02", "Roberto Salas"],
  ["G-03", "Patricia Núñez"],
  ["G-04", "Alberto Quiroz"],
  ["G-05", "Verónica Lara"],
  ["G-06", "Samuel Ochoa"],
];

const CIUDADES = [
  "Centro", "Norte", "Sur", "Poniente", "Oriente", "Aeropuerto", "Plaza Galerías",
  "Valle", "Lomas", "Satélite", "Coyoacán", "Polanco", "Tlalpan", "Querétaro",
  "Puebla", "León", "Toluca", "Monterrey", "Saltillo", "Mérida", "Cancún",
  "Guadalajara", "Zapopan", "Morelia", "Aguascalientes", "Hermosillo", "Chihuahua",
  "Veracruz", "Oaxaca", "Tijuana",
] as const;

// Sucursales nuevas (sin meta ni historia) y en remodelación (sin clientes).
const NUEVAS = new Set([4, 13, 22]);
const REMODELACION = new Set([9, 26]);
const SIN_GERENTE = new Set([7, 18, 27]);

const rnd = seededRandom(9001);
// Sucursal, Ventas 2026, Ventas 2025, Meta, Clientes, Código gerente
const ROWS = CIUDADES.map((ciudad, i) => {
  const remodel = REMODELACION.has(i);
  const nueva = NUEVAS.has(i);
  const v25 = nueva ? 0 : rnd.round(400000, 2500000, 1000);
  const v26 = remodel ? 0 : nueva ? rnd.round(150000, 600000, 1000) : Math.round(v25 * rnd.int(80, 130) / 100);
  const meta = nueva ? 0 : rnd.round(500000, 2400000, 10000);
  const clientes = remodel ? 0 : rnd.int(800, 9000);
  const codigo = SIN_GERENTE.has(i) ? `G-0${7 + (i % 3)}` : rnd.pick(GERENTES)[0];
  return [`Sucursal ${ciudad}`, v26, v25, meta, clientes, codigo] as [
    string, number, number, number, number, string,
  ];
});

export const spec: FuncionProjectSpec = {
  projectId: "funciones-si-error",
  sheets: [
    {
      name: SHEET,
      title: "Reporte de sucursales",
      subtitle: "Reporte mensual para dirección — que no quede ni un #DIV/0! ni un #N/D",
      tables: [
        {
          name: TABLE,
          anchor: "A4",
          headers: ["Sucursal", "Ventas 2026", "Ventas 2025", "Meta", "Clientes", "Código gerente"],
          rows: ROWS,
          answerColumns: ["Avance", "Ticket promedio", "Crecimiento", "Gerente", "Comentario"],
          formats: {
            "Ventas 2026": "$#,##0",
            "Ventas 2025": "$#,##0",
            Meta: "$#,##0",
            Avance: "0%",
            "Ticket promedio": "$#,##0.00",
            Crecimiento: "0%",
          },
        },
        {
          name: GERENTES_TABLE,
          anchor: "N4",
          headers: ["Código", "Gerente"],
          rows: GERENTES,
        },
      ],
    },
  ],
  checks: {
    1: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Avance", fns: ["IFERROR"],
      expected: ROWS.map((r) => (r[3] === 0 ? 0 : r[1] / r[3])),
    },
    2: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Ticket promedio", fns: ["IFERROR"],
      expected: ROWS.map((r) => (r[4] === 0 ? "Sin clientes" : r[1] / r[4])),
    },
    3: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Crecimiento", fns: ["IFERROR"],
      expected: ROWS.map((r) => (r[2] === 0 ? "Nueva" : r[1] / r[2] - 1)),
    },
    4: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Gerente", fns: ["IFNA", "VLOOKUP|XLOOKUP"],
      expected: ROWS.map((r) => GERENTES.find((g) => g[0] === r[5])?.[1] ?? "Sin asignar"),
    },
    5: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Comentario", fns: ["IFERROR", "IF"],
      expected: ROWS.map((r) =>
        r[3] === 0 ? "Sucursal nueva" : r[1] / r[3] >= 1 ? "Meta cumplida" : "Debajo de meta"
      ),
    },
  },
};

const F = (text: string) => `<span class="formula">${text}</span>`;

export const project: ProjectDefinition = {
  id: "funciones-si-error",
  title: "SI.ERROR y SI.ND: Reporte sin errores",
  subtitle: "Errores controlados con mensajes claros",
  description:
    "Vas a entregar el reporte mensual de 30 sucursales a dirección. Hay sucursales nuevas sin meta, otras en remodelación sin clientes y códigos de gerente que no existen en el directorio.\n\nEn lugar de #DIV/0! y #N/D, el reporte debe mostrar valores o mensajes que cualquiera entienda.",
  topics: [
    "SI.ERROR para divisiones entre cero",
    "Devolver un número o un texto ante el error",
    "SI.ND solo para búsquedas sin resultado",
    "SI.ERROR envolviendo un SI",
  ],
  tasks: [
    {
      id: 1,
      level: "basico",
      title: "Tarea 1: Avance contra la meta",
      description:
        "En la hoja <strong>LG Reporte</strong>, columna <strong>Avance</strong>: divide <strong>Ventas 2026</strong> entre <strong>Meta</strong>. Las sucursales nuevas tienen meta 0 y darían <strong>#DIV/0!</strong>: en ese caso devuelve <strong>0</strong>.",
      solution: [
        `Selecciona ${F("G5")}.`,
        "SI.ERROR(valor, valor_si_error) devuelve el cálculo normal, o el segundo argumento si el cálculo produce cualquier error.",
        `Escribe ${F("=SI.ERROR([@[Ventas 2026]]/[@Meta],0)")}.`,
      ],
      expectedResult: "Porcentaje de avance, o 0 en sucursales nuevas",
      finalFormula: "=SI.ERROR([@[Ventas 2026]]/[@Meta],0)",
    },
    {
      id: 2,
      level: "basico",
      title: "Tarea 2: Ticket promedio",
      description:
        "En la columna <strong>Ticket promedio</strong>: divide <strong>Ventas 2026</strong> entre <strong>Clientes</strong>. Si hay error (sucursales en remodelación), muestra el texto <strong>Sin clientes</strong>.",
      solution: [
        `Selecciona ${F("H5")}.`,
        `Escribe ${F('=SI.ERROR([@[Ventas 2026]]/[@Clientes],"Sin clientes")')}.`,
      ],
      expectedResult: "Monto promedio por cliente, o Sin clientes",
      finalFormula: '=SI.ERROR([@[Ventas 2026]]/[@Clientes],"Sin clientes")',
    },
    {
      id: 3,
      level: "basico",
      title: "Tarea 3: Crecimiento anual",
      description:
        "En la columna <strong>Crecimiento</strong>: calcula <strong>Ventas 2026 ÷ Ventas 2025 − 1</strong>. Las sucursales sin ventas en 2025 deben mostrar <strong>Nueva</strong>.",
      solution: [
        `Selecciona ${F("I5")}.`,
        `Escribe ${F('=SI.ERROR([@[Ventas 2026]]/[@[Ventas 2025]]-1,"Nueva")')}.`,
      ],
      expectedResult: "Porcentaje de crecimiento, o Nueva",
      finalFormula: '=SI.ERROR([@[Ventas 2026]]/[@[Ventas 2025]]-1,"Nueva")',
    },
    {
      id: 4,
      level: "intermedio",
      title: "Tarea 4: Nombre del gerente",
      description:
        "En la columna <strong>Gerente</strong>: busca el <strong>Código gerente</strong> en la tabla <strong>Gerentes</strong> (N4) con <strong>BUSCARV</strong> o <strong>BUSCARX</strong>. Si el código no existe, muestra <strong>Sin asignar</strong> usando <strong>SI.ND</strong>.",
      solution: [
        `Selecciona ${F("J5")}.`,
        "Cuando una búsqueda no encuentra el valor devuelve #N/D. SI.ND atrapa solo ese error: si hubiera otro (por ejemplo, un rango mal escrito) lo seguirías viendo, y eso es bueno.",
        `Escribe ${F('=SI.ND(BUSCARV([@[Código gerente]],Gerentes,2,FALSO),"Sin asignar")')}.`,
      ],
      expectedResult: "Nombre del gerente, o Sin asignar",
      finalFormula: '=SI.ND(BUSCARV([@[Código gerente]],Gerentes,2,FALSO),"Sin asignar")',
    },
    {
      id: 5,
      level: "reto",
      title: "Tarea 5: Comentario para dirección",
      description:
        conditions(
        "En la columna <strong>Comentario</strong>, según Ventas 2026 ÷ Meta:",
        [
          "<strong>1 o más</strong> → <strong>Meta cumplida</strong>",
          "<strong>Menos de 1</strong> → <strong>Debajo de meta</strong>",
          "Si la división da <strong>error</strong> (meta 0) → <strong>Sucursal nueva</strong>",
        ],
        "Envuelve un <strong>SI</strong> con <strong>SI.ERROR</strong>."
      ),
      solution: [
        `Selecciona ${F("K5")}.`,
        "Primero arma el SI normal. Luego envuélvelo: si la prueba lógica del SI da error, SI.ERROR responde por él.",
        `Escribe ${F('=SI.ERROR(SI([@[Ventas 2026]]/[@Meta]>=1,"Meta cumplida","Debajo de meta"),"Sucursal nueva")')}.`,
      ],
      expectedResult: "Meta cumplida / Debajo de meta / Sucursal nueva",
      finalFormula:
        '=SI.ERROR(SI([@[Ventas 2026]]/[@Meta]>=1,"Meta cumplida","Debajo de meta"),"Sucursal nueva")',
    },
  ],
};
